import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useNavigate, useParams, useLocation, useNavigationType } from 'react-router-dom';
import { Client } from 'colyseus.js';
import { Crown, Trophy, Clock, TrendingUp, Users, Calculator as CalculatorIcon, X, Check } from 'lucide-react';
import api, { getAuthIdToken } from '../api';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';
import Calculator from '../components/Calculator';

// Animated Number component
const AnimatedNumber = ({ value, fromValue, duration = 2500, easing = 'easeOutCubic', format }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValueRef = useRef(value);
  const rafRef = useRef(null);

  const prefersReducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const startValue = (typeof fromValue === 'number') ? fromValue : previousValueRef.current;
    const endValue = value;

    if (prefersReducedMotion || startValue === endValue) {
      setDisplayValue(endValue);
      previousValueRef.current = endValue;
      return;
    }

    const ease = (t) => {
      if (easing === 'easeOutExpo') return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      if (easing === 'linear') return t;
      return 1 - Math.pow(1 - t, 3);
    };

    const startTime = performance.now();
    cancelAnimationFrame(rafRef.current);

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = ease(progress);
      const current = startValue + (endValue - startValue) * eased;
      setDisplayValue(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        previousValueRef.current = endValue;
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, fromValue, duration, easing, prefersReducedMotion]);

  const formatter = format || ((n) => Math.round(n));

  return (
    <span>{formatter(displayValue)}</span>
  );
};

// Simple Card component with hover effect only - doesn't interfere with inputs
const Card = ({ children, className = '', delay = 0 }) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [hasInputFocusState, setHasInputFocusState] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Track input focus state with event listeners
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const checkInputFocus = () => {
      const activeElement = document.activeElement;
      const hasFocus = card.contains(activeElement) && 
                       (activeElement.tagName === 'INPUT' || 
                        activeElement.tagName === 'TEXTAREA' || 
                        activeElement.tagName === 'SELECT');
      setHasInputFocusState(hasFocus);
      if (hasFocus) {
        setIsHovered(false);
      }
    };

    // Check on focus/blur events
    card.addEventListener('focusin', checkInputFocus);
    card.addEventListener('focusout', checkInputFocus);
    
    // Also check periodically to catch any edge cases
    const interval = setInterval(checkInputFocus, 100);

    return () => {
      card.removeEventListener('focusin', checkInputFocus);
      card.removeEventListener('focusout', checkInputFocus);
      clearInterval(interval);
    };
  }, []);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={hasInputFocusState ? {} : {
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => {
        if (!hasInputFocusState) setIsHovered(true);
      }}
      onHoverEnd={() => setIsHovered(false)}
      onMouseMove={(e) => {
        // Disable hover if mouse is over input elements
        const target = e.target;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' ||
            target.closest('input, textarea, select')) {
          setIsHovered(false);
          return;
        }
      }}
      className={`relative backdrop-blur-xl rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${!className.match(/\bbg-\w+/) ? 'bg-white/80 dark:bg-gray-800/80' : ''} ${className}`}
      style={{
        boxShadow: isHovered && !hasInputFocusState
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered && !hasInputFocusState
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

const MultiplayerGameRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  // Get password from location state if available
  const roomPassword = location.state?.roomPassword || null;
  const baseUrl = process.env.REACT_APP_COLYSEUS_URL;
  const { isDarkMode } = useContext(ThemeContext);
  
  // Get user from localStorage
  const userStr = typeof window !== 'undefined' ? localStorage.getItem('sat_user') : null;
  const user = userStr ? JSON.parse(userStr) : null;
  
  // connection/game state
  const [client, setClient] = useState(null);
  const [room, setRoom] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  // gameplay
  const [gameActive, setGameActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionType, setQuestionType] = useState('');
  const [questionCategoryType, setQuestionCategoryType] = useState(''); // Store category_type from question
  const [options, setOptions] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(10);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [players, setPlayers] = useState([]);
  const [roomName, setRoomName] = useState('');
  const [subject, setSubject] = useState('');
  const [difficulty, setDifficulty] = useState('');
  // lock leaving between rounds
  const [leaveLocked, setLeaveLocked] = useState(false);


  // NEW: Transition state variables
  const [showRoundWinnerTransition, setShowRoundWinnerTransition] = useState(false);
  const [roundWinnerPlayer, setRoundWinnerPlayer] = useState(null);
  const [questionResultSummary, setQuestionResultSummary] = useState(null);
  const [summaryCountdown, setSummaryCountdown] = useState(null);
  const roomContainerRef = useRef(null); // for stray clicks oustide game room
  const inactivityTimerRef = useRef(null); // for player inactivity
  const [showEndSessionModal, setShowEndSessionModal] = useState(false); //copy EndSessionModal from PlayGamePage.js

  // per-question context
  const [questionActive, setQuestionActive] = useState(false);
  const [scoreChanges, setScoreChanges] = useState({});
  const [previousPlayerStates, setPreviousPlayerStates] = useState([]);
  const [finalPlayers, setFinalPlayers] = useState([]);
  const [hasCompletedFirstQuestion, setHasCompletedFirstQuestion] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [roundWinner, setRoundWinner] = useState(null);
  //Final scoreboard styling borrowed from LeaderboardPage.css uses table layout & fonts

  const [roundDeltas, setRoundDeltas] = useState({});
  const roundDeltasRef = useRef({});
  const lastScoresRef = useRef({});

  // Freeze timer for question
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [submitCountdown, setSubmitCountdown] = useState(10);

  // Current question difficulty for animated display
  const [currentQuestionDifficulty, setCurrentQuestionDifficulty] = useState('');
  
  // Animated points counter state
  const [animatedPoints, setAnimatedPoints] = useState(0);

  // Calculator state
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [eliminatedOptions, setEliminatedOptions] = useState(new Set());
  
  // Visual effects for correct/incorrect answers
  const [showWrongEffect, setShowWrongEffect] = useState(false);
  const [showCorrectEffect, setShowCorrectEffect] = useState(false);
  const wrongSfxRef = useRef(null);

  // Function to get points at stake based on difficulty
  const getPointsAtStake = () => {
    const qDiff = currentQuestionDifficulty?.toLowerCase() || difficulty?.toLowerCase() || 'easy';
    switch (qDiff) {
      case 'easy': return 100;
      case 'medium': return 200;
      case 'hard': return 300;
      default: return 100;
    }
  };

  // Function to get glow color based on difficulty (matching PlayGamePage.js)
  const getDifficultyGlow = () => {
    const qDiff = currentQuestionDifficulty?.toLowerCase() || difficulty?.toLowerCase() || 'easy';
    switch (qDiff) {
      case 'easy':
        return '0 0 30px rgba(34, 197, 94, 0.6), 0 2px 2px rgba(0,0,0,0.1)'; // Green glow
      case 'medium':
        return '0 0 30px rgba(234, 179, 8, 0.6), 0 2px 2px rgba(0,0,0,0.1)'; // Yellow glow
      case 'hard':
        return '0 0 30px rgba(239, 68, 68, 0.6), 0 2px 2px rgba(0,0,0,0.1)'; // Red glow
      default:
        return '0 2px 2px rgba(0,0,0,0.1)';
    }
  };

  // Function to get difficulty color classes
  const getDifficultyColors = () => {
    const qDiff = currentQuestionDifficulty?.toLowerCase() || difficulty?.toLowerCase() || 'easy';
    switch (qDiff) {
      case 'easy':
        return { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-300', border: 'border-green-500 dark:border-green-600', glow: 'shadow-green-500' };
      case 'medium':
        return { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-300', border: 'border-yellow-500 dark:border-yellow-600', glow: 'shadow-yellow-500' };
      case 'hard':
        return { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300', border: 'border-red-500 dark:border-red-600', glow: 'shadow-red-500' };
      default:
        return { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-white', border: 'border-gray-300 dark:border-gray-600', glow: 'shadow-gray-500' };
    }
  };

  // Close calculator if question changes to non-math based on category_type
  useEffect(() => {
    if (currentQuestion) {
      // Use category_type from the question to determine if it's math
      const categoryTypeLower = questionCategoryType?.toLowerCase() || '';
      const isMathCategory = categoryTypeLower === 'math' || 
                            categoryTypeLower.includes('math') ||
                            categoryTypeLower === 'algebra' ||
                            categoryTypeLower === 'advanced math' ||
                            categoryTypeLower === 'geometry and trigonometry' ||
                            categoryTypeLower === 'problem solving and data analysis';
      
      // Close calculator if switching to non-math question
      if (!isMathCategory && isCalculatorOpen) {
        setIsCalculatorOpen(false);
      }
    }
  }, [questionNumber, questionCategoryType, isCalculatorOpen, currentQuestion]);

  // Animate points counter when question/difficulty changes
  useEffect(() => {
    if (gameActive && currentQuestion) {
      const targetPoints = getPointsAtStake();
      setAnimatedPoints(0);
      
      const duration = 800;
      const steps = 30;
      const increment = targetPoints / steps;
      const stepDuration = duration / steps;
      
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.round(increment * currentStep), targetPoints);
        setAnimatedPoints(newValue);
        
        if (currentStep >= steps) {
          clearInterval(timer);
          setAnimatedPoints(targetPoints);
        }
      }, stepDuration);
      
      return () => clearInterval(timer);
    }
  }, [questionNumber, currentQuestionDifficulty, difficulty, gameActive, currentQuestion]);

  // Player ID state (needed for multiplayer functionality)
  const [myId, setMyId] = useState('');
  const myIdRef = useRef('');
  useEffect(() => {
    myIdRef.current = myId;
  }, [myId]);

  // Total score from API (overall score across all games)
  const [totalScore, setTotalScore] = useState(0);

  // Current user's score in this room
  const myRoomScore = useMemo(() => {
    const me = players.find(p => p.id === myId);
    return me && typeof me.score === 'number' ? me.score : 0;
  }, [players, myId]);

  //Track players who answered correct to show trophy at start of question phase
  const [recentCorrectIds, setRecentCorrectIds] = useState({});
  const lastScoreChangesRef = useRef({});
  useEffect(() => { lastScoreChangesRef.current = scoreChanges || {}; }, [scoreChanges]);


  // Function to fetch total score for a player
  const fetchTotalScore = async () => {
    try {
      const response = await api.get(`/api/user/get_total_score/`);
      return response.data.total_score || 0;
    } catch (error) { 
      console.error('Error fetching total score:', error);
      return 0;
    }
  };

  // Fetch total score when user is available
  useEffect(() => {
    const loadTotalScore = async () => {
      if (user?.user_id) {
        try {
          const score = await fetchTotalScore();
          setTotalScore(score);
        } catch (error) {
          console.error('Error loading total score:', error);
          setTotalScore(0);
        }
      } else {
        setTotalScore(0);
      }
    };
    loadTotalScore();
  }, [user?.user_id]);

  // Sound effects refs
  const correctSfxRef = useRef(null);
  const lastConfettiQuestionRef = useRef(-1);

  // Preload correct answer sound effect from public path
  useEffect(() => {
    try {
      if (typeof Audio !== 'undefined') {
        const audio = new Audio('/correct_answer.mp3');
        audio.preload = 'auto';
        audio.volume = 0.6;
        correctSfxRef.current = audio;
      }
    } catch {}
    return () => {
      try {
        if (correctSfxRef.current) {
          correctSfxRef.current.pause();
          correctSfxRef.current.src = '';
          correctSfxRef.current = null;
        }
      } catch {}
    };
  }, []);

  // Preload wrong answer buzz sound
  useEffect(() => {
    try {
      if (typeof Audio !== 'undefined') {
        const audio = new Audio('/wrong_answer.mp3');
        audio.preload = 'auto';
        audio.volume = 0.7;
        wrongSfxRef.current = audio;
      }
    } catch {}
    return () => {
      try {
        if (wrongSfxRef.current) {
          wrongSfxRef.current.pause();
          wrongSfxRef.current.src = '';
          wrongSfxRef.current = null;
        }
      } catch {}
    };
  }, []);

  const triggerCorrectEffects = async () => {
    // Show green tick mark animation
    setShowCorrectEffect(true);
    setTimeout(() => {
      setShowCorrectEffect(false);
    }, 1000); // Hide after 1 second
    
    // Confetti
    try {
      const mod = await import('canvas-confetti');
      const confetti = mod.default || mod;
      const colors = ['#36e338', '#47d2d3', '#22c55e', '#06b6d4'];
      confetti({
        particleCount: 120,
        spread: 70,
        startVelocity: 45,
        scalar: 0.9,
        zIndex: 10000,
        origin: { y: 0.6 },
        colors,
      });
      setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, startVelocity: 35, scalar: 0.9, zIndex: 10000, origin: { x: 0, y: 0.7 }, colors }), 100);
      setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, startVelocity: 35, scalar: 0.9, zIndex: 10000, origin: { x: 1, y: 0.7 }, colors }), 100);
    } catch {}
    // Sound
    try {
      const a = correctSfxRef.current;
      if (a) {
        a.currentTime = 0;
        a.play().catch(() => {});
      }
    } catch {}
  };

  const triggerWrongEffects = async () => {
    // Show X mark animation
    setShowWrongEffect(true);
    setTimeout(() => {
      setShowWrongEffect(false);
    }, 1000); // Hide after 1 second
    
    // Buzz sound
    try {
      const a = wrongSfxRef.current;
      if (a) {
        a.currentTime = 0;
        a.play().catch(() => {});
      }
    } catch {}
  };
  
  // Player name
  const playerName = useMemo(() => {
    if (!user) return 'Player';
    // Use screen_name as the primary choice, fallback to other names
    const c = [user.screenName, user.screen_name, user.user_name, user.username, user.name].find(Boolean);
    if (c) return c;
    if (user.email && typeof user.email === 'string') {
      const local = user.email.split('@')[0];
      if (local) return local;
    }
    return 'Player';
  }, [user]);

  const updateRoom = async (adminId, roomId, status, startedAt = null, endedAt = null, winnerUserId = null) => {
    try {
      const update_room = await api.post(`/api/compete/update_room/`, {
        room_id: roomId,
        status: status,
        started_at: startedAt,
        ended_at: endedAt,
        winner_user: winnerUserId,
      });
      console.log('update_room success', update_room);
    } catch (error) {
      console.error('Failed to update room:', error);
    }
  };

  // After each players update, record their latest scores
  useEffect(() => {
    try {
      const snapshot = {};
      players.forEach((p) => { snapshot[p.id] = typeof p.score === 'number' ? p.score : 0; });
      lastScoresRef.current = snapshot;
    } catch {}
  }, [players]);
  const updateRoomMember = async (roomId, rankInRoom, score, accuracy) => {
    try {
      const update_room_member = await api.post(`/api/compete/update_room_member/`, {
        room_id: roomId,
        is_host: null,
        rank_in_room: rankInRoom,
        score: score,
        accuracy: accuracy,
      });
      console.log('update_room_member success', update_room_member);
      updateRankInRoom(roomId);
    } catch (error) {
      console.error('Failed to update room member:', error);
    }
  };
  const updateRankInRoom = async (roomId) => {
    try {
      const update_rank_in_room = await api.post(`/api/compete/update_rank_in_room/`, {
        room_id: roomId,
      });
      console.log('update_rank_in_room success', update_rank_in_room);
    } catch (error) {
    console.error('Failed to update rank in room:', error);
    }
  };
  // Helper function for sorting players with tie-breaking by accuracy
  const sortPlayersByScoreAndAccuracy = (players) => {
    return players.sort((a, b) => {
      // First, sort by score (descending)
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      // If scores are equal, sort by accuracy (descending)
      const accuracyA = parseFloat(a.accuracy) || 0;
      const accuracyB = parseFloat(b.accuracy) || 0;
      return accuracyB - accuracyA;
    });
  };

  // Check if user has permission to access this game room
  useEffect(() => {
    const { canAccessGameRoom, fromWaitingRoom, roomId: stateRoomId } = location.state || {};

    // If page was reached via browser back/forward (POP), block access
    if (navigationType === 'POP') {
      navigate('/lobby-multiplayer', {
        replace: true,
        state: { error: 'Access denied. Please join through the waiting room.' }
      });
      return;
    }

    // Check if user came from waiting room and room ID matches
    if (!canAccessGameRoom || !fromWaitingRoom || stateRoomId !== roomId) {
      navigate('/lobby-multiplayer', {
        replace: true,
        state: { error: 'Access denied. Please join through the waiting room.' }
      });
      return;
    }
  }, [roomId, navigate, location.state, navigationType]);

  // countdown to submit
  useEffect(() => {
    // reset and start countdown whenever a new question loads
    setSubmitDisabled(true);
    setSubmitCountdown(10);

    const interval = setInterval(() => {
      setSubmitCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setSubmitDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentQuestion]); // depends on the current question

  // connect to existing room
  useEffect(() => {
    let mounted = true;
    const preload = async () => {
      try {
        const res = await fetch(`${baseUrl}/rooms/${encodeURIComponent(roomId)}`);
        if (res.ok) {
          const data = await res.json();
          if (!mounted) return;
          setRoomName(data.roomName || 'Multiplayer Game');
          setSubject(data.subject || '—');
          setDifficulty(data.difficulty || 'Easy');
        }
      } catch {}
    };

    const doConnect = async () => {
      try {
        setIsConnecting(true);
        // If a previous page persisted a live connection, reuse it
        const persistedRoom = typeof window !== 'undefined' ? window.__colyseusRoom : null;
        const persistedClient = typeof window !== 'undefined' ? window.__colyseusClient : null;
        if (persistedRoom && persistedClient && window.__colyseusRoomId === roomId) {
          setClient(persistedClient);
          setRoom(persistedRoom);
          setIsConnected(true);
          setMyId(persistedRoom.sessionId);
          attachHandlers(persistedRoom);
          setIsConnecting(false);
          return;
        }

        const c = new Client(process.env.REACT_APP_COLYSEUS_WS_URL);
        console.log('c', c);
        setClient(c);
        const authToken = await getAuthIdToken();
        if (!authToken) throw new Error('Your session has expired. Sign in again.');
        const r = await c.joinById(roomId, {
          playerName,
          authToken,
          password: roomPassword || undefined,
        });
        if (!mounted) return;
        setRoom(r);
        setIsConnected(true);
        setMyId(r.sessionId);
        attachHandlers(r);
      } catch (e) {
        setError(e.message || 'Failed to connect');
      } finally {
        setIsConnecting(false);
      }
    };

    preload();
    doConnect();
    return () => {
      mounted = false;
      try { room && room.leave(); } catch {}
    };
  }, [roomId]);

  useEffect(() => {
    // Show exit confirmation message when player trys to close tab
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // Show exit confirmation when player clicks outside of game room container.
    const handleOutsideClick = (event) => {
      if (
        roomContainerRef.current &&
        !roomContainerRef.current.contains(event.target) &&
        !showEndSessionModal
      ) {
        setShowEndSessionModal(true);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showEndSessionModal]);

  useEffect(() => {
    // show exit confirmation when player is inactive for certain time.
   const handleInactivity = () => {
        setShowEndSessionModal(true);
        
    };

    const resetInactivityTimer = () => {
        clearTimeout(inactivityTimerRef.current);
        // Set the timer to temporary duration 60 mins (60000 milliseconds)
        inactivityTimerRef.current = setTimeout(handleInactivity, 60000); 
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];

    const addListeners = () => {
        events.forEach(event => window.addEventListener(event, resetInactivityTimer));
        resetInactivityTimer(); // Clear timer
    };

    const removeListeners = () => {
        events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
        clearTimeout(inactivityTimerRef.current);
    };

    if (gameActive) {
        addListeners();
    } else {
        removeListeners();
    }

    return () => {
        removeListeners();
    };

  }, [gameActive]);

  // Countdown timer for redirecting to report page
  useEffect(() => {
    if (showCountdown && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showCountdown && countdown === 0) {
      // Close the room before redirecting
      if (room && isConnected) {
        try {
          room.send('closeRoom', {});
          console.log('Sent closeRoom message to Colyseus');
        } catch (error) {
          console.error('Error sending closeRoom message:', error);
        }
      }
      
      // Redirect to report page after countdown
      navigate(`/multiplayer/report/${roomId}`, {
        state: {
          gameData: {
            roomId: roomId,
            players: finalPlayers,
            gameInfo: {
              roomName: roomName,
              subject: subject,
              difficulty: difficulty
            }
          },
          fromMultiplayerGameRoom: true
        }
      });
    }
  }, [showCountdown, countdown, navigate, roomId, finalPlayers, roomName, subject, difficulty]);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      setShowCountdown(false);
      setCountdown(5);
    };
  }, []);

  const attachHandlers = (r) => {
    // Prevent duplicate handler attachment if reused across pages
    if (r.__handlersAttachedGame) {
      // Still prime state once
      primeFromState(r);
      return;
    }
    r.__handlersAttachedGame = true;

    // Prime UI from current state in case we missed 'newQuestion'
    primeFromState(r);

    // live players from room state
    r.onStateChange((state) => {
      const live = [];
      try {
        if (state && state.players && typeof state.players.forEach === 'function') {
          state.players.forEach((p, id) => {
            live.push({
              id, // Colyseus session ID
              userId: p.userId, // Django user ID
              name: p.name,
              score: p.score,
              purse: p.purse,
              correctAnswers: p.correctAnswers,
              totalAnswers: p.totalAnswers,
              accuracy: p.totalAnswers > 0 ? (p.correctAnswers / p.totalAnswers * 100).toFixed(1) : '0',
              waiting: p.waiting,
            });
          });
        }
      } catch {}
      setPlayers(live);
    });

    // game events
    let admin = null;
    r.onMessage('welcome', async (data) => {
      console.log('Welcome message received:', data);
      // Use total score from backend if available, otherwise fetch it
      if (data.totalScore !== undefined) {
        console.log('Total score from backend:', data.totalScore);
        // The purse will be updated when the player state is received
      } else if (user?.user_id) {
        try {
          const totalScore = await fetchTotalScore();
          console.log('Fetched total score for user:', totalScore);
          // The purse will be updated when the player state is received
        } catch (error) {
          console.error('Failed to fetch total score:', error);
        }
      }
    });
    r.onMessage('gameStarted', (data) => {
    
      setGameActive(true);
      setResult(null);
      setHasCompletedFirstQuestion(false);
      // Update room status to in_progress when game starts
      console.log('gameStarted', data.adminId);
      admin = data.adminId;
      if (user?.user_id && admin===user.user_id) {
        updateRoom(user.user_id, roomId, 'in_progress', new Date().toISOString(), null, null);
      }
      
      // hydrate players directly from current state to avoid empty list
      try {
        const live = [];
        if (r.state && r.state.players && typeof r.state.players.forEach === 'function') {
          r.state.players.forEach((p, id) => {
            live.push({
              id,
              name: p.name,
              score: 0,
              purse: p.purse,
              correctAnswers: 0,
              totalAnswers: 0,
              accuracy: '0',
            });
          });
        }
        if (live.length > 0) setPlayers(live);
      } catch {}
    });

    // Keep player list responsive if state sync lags
    r.onMessage('playerJoined', ({ playerId, playerName }) => {
      setPlayers((prev) => {
        const exists = prev.some(p => p.id === playerId);
        if (exists) return prev;
        return [...prev, { id: playerId, name: playerName, score: 0, correctAnswers: 0, totalAnswers: 0, accuracy: '0' }];
      });
    });
    r.onMessage('playerLeft', ({ playerId }) => {
      setPlayers((prev) => prev.filter(p => p.id !== playerId));
    });

    // NEW: Handle question summary message from server
    r.onMessage('questionSummary', (data) => {
      setQuestionResultSummary({
        ...data,
        roundDeltas: roundDeltasRef.current, // guaranteed up-to-date
      });
    
      let seconds = 5;
      setSummaryCountdown(seconds);
      const summaryTimeout = setInterval(() => {
        seconds -= 1;
        setSummaryCountdown(seconds);
        if (seconds <= 0) {
          clearInterval(summaryTimeout);
          setQuestionResultSummary(null);
          setRoundDeltas({});
          roundDeltasRef.current = {}; // reset ref too
        }
      }, 1000);
    });

    r.onMessage('newQuestion', (data) => {
      setLeaveLocked(false); // next question arrived; allow leaving again
      setQuestionActive(true);
      setScoreChanges({});
      setRoundDeltas({});
      // Clear winner transition state
      setShowRoundWinnerTransition(false);
      setRoundWinnerPlayer(null);
      setRoundWinner(null); // Clear previous round winner
      
      // Set current question difficulty for animated display
      try {
        const qDiff = (data && data.difficulty_level) ? String(data.difficulty_level).toLowerCase() : '';
        setCurrentQuestionDifficulty(qDiff);
      } catch {}
      
      try {
        const snap = [];
        if (r.state && r.state.players && typeof r.state.players.forEach === 'function') {
          r.state.players.forEach((p, id) => {
            snap.push({
              id,
              name: p.name,
              score: p.score,
              correctAnswers: p.correctAnswers,
              totalAnswers: p.totalAnswers,
              accuracy: p.totalAnswers > 0 ? (p.correctAnswers / p.totalAnswers * 100).toFixed(1) : '0',
            });
          });
        }
        setPreviousPlayerStates(snap);
      } catch { setPreviousPlayerStates([]); }
      setCurrentQuestion(data.question);
      setQuestionType(data.questionType);
      setQuestionCategoryType(data.category_type || ''); // Store category_type from question
      setOptions({ A: data.optionA, B: data.optionB, C: data.optionC, D: data.optionD });
      setQuestionNumber(data.questionNumber);
      setTimeRemaining(data.timeLimit);
      setAnswer('');
      setResult(null);
      setEliminatedOptions(new Set()); // Reset eliminated options for new question
    });
    r.onMessage('nextRoundReady', (data) => {
      // Player who was waiting can now participate in the next question
      console.log('[CLIENT] nextRoundReady received - player can now participate');
      // Clear any old question state so waiting player doesn't see previous question
      setCurrentQuestion(null);
      setQuestionActive(false);
      // The player's waiting state has been set to false on the server
      // Refresh state to get updated player info
      primeFromState(r);
      // The new question will arrive via newQuestion message immediately after
    });
    r.onMessage('timeUpdate', (data) => {
      setTimeRemaining(data.remaining);
      // Ensure question UI is populated even if we joined mid-question
      primeFromState(r);
    });
    r.onMessage('answerResult', (data) => {
      setResult({
        correct: data.correct,
        points: data.points,
        correctAnswer: data.correctAnswer,
        feedback: data.feedback
      });
      setLeaveLocked(true); // lock leaving until next question appears
      
      // Trigger visual and audio effects
      if (data.correct) {
        triggerCorrectEffects();
      } else {
        triggerWrongEffects();
      }
    });

    r.onMessage('scoreChange', (data) => {
      setScoreChanges((prev) => ({
        ...prev,
        [data.playerId]: {
          delta: ((prev && prev[data.playerId] && typeof prev[data.playerId].delta === 'number') ? prev[data.playerId].delta : 0) + data.delta,
          isCorrect: !!data.isCorrect,
        },
      }));

      // update state + ref
      setRoundDeltas((prev) => {
        const updated = {
          ...prev,
          [data.playerId]: (prev[data.playerId] || 0) + data.delta,
        };
        roundDeltasRef.current = updated;
        return updated;
      });

      // Update total score if this is the current player
      // Use ref to get the current value since handler might have stale closure
      if (data.playerId === myIdRef.current) {
        setTotalScore((prev) => {
          const newTotal = prev + data.delta;
          return newTotal;
        });
      }
    });
    r.onMessage('roundWinner', (data) => {
      console.log('Round winner received:', data);
      setRoundWinner(data.winnerName);
      // Clear winner after 3 seconds
      setTimeout(() => setRoundWinner(null), 3000);
    });
    r.onMessage('questionEnded', () => {
      setLeaveLocked(true);
      setQuestionActive(false);
      setCurrentQuestion(null);
      setTimeRemaining(0);
      setHasCompletedFirstQuestion(true);
    });
    r.onMessage('gameEnded', (data) => {
      setLeaveLocked(false); // allow leaving on final scoreboard
      setGameActive(false);
      setCurrentQuestion(null);
      // snapshot final players 
      // for results scoreboard
      setSummaryCountdown(null);
      
      console.log('gameEnded', data);
      
      // Update room status to completed with winner when game ends
      console.log('gameEnded adminId', data.adminId);
      if (user?.user_id && data?.winner && data.adminId && String(data.adminId) === String(user.user_id)) {
        updateRoom(user.user_id, roomId, 'completed', null, new Date().toISOString(), data.winner.userId);
      }
        
      // Use the sorted players data from Colyseus backend
      if (data?.players && Array.isArray(data.players)) {
        const sortedPlayers = data.players.map((player, index) => ({
          id: player.sessionId,
          userId: player.userId,
          name: player.name,
          score: player.score,
          purse: player.purse,
          correctAnswers: player.correctAnswers,
          totalAnswers: player.totalAnswers,
          accuracy: player.accuracy,
          ranked: index + 1
        }));
        if (data.adminId && String(data.adminId) === String(user.user_id)) {
          sortedPlayers.forEach((player) => {
            updateRoomMember(player.userId, roomId, player.ranked, player.score, player.accuracy);
          });
        }
        setFinalPlayers(sortedPlayers);
        console.log('Final players (sorted by score from backend):', sortedPlayers);
      } else {
        // Fallback to snapshot from state if backend data not available
      try {
        const st = r.state;
        const live = [];
        if (st && st.players && typeof st.players.forEach === 'function') {
            st.players.sort((a, b) => {
      // First, sort by score (descending)
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      // If scores are equal, sort by accuracy (descending)
      const accuracyA = parseFloat(a.accuracy) || 0;
      const accuracyB = parseFloat(b.accuracy) || 0;
      return accuracyB - accuracyA;
    }).forEach((p, index) => {
            live.push({
              name: p.name,
              score: p.score,
              correctAnswers: p.correctAnswers,
              totalAnswers: p.totalAnswers,
                accuracy: p.totalAnswers > 0 ? (p.correctAnswers / p.totalAnswers * 100).toFixed(1) : '0',
                ranked: index + 1
            });
          });
        }
          if (data.adminId && String(data.adminId) === String(user.user_id)) {
            live.forEach((player) => {
              updateRoomMember(player.userId, roomId, player.ranked, player.score, player.accuracy);
          });
        }
        setFinalPlayers(live);
      } catch {
        console.log('error', error);
      }
    }
    
    // Start countdown for redirect to report page
    setShowCountdown(true);
    setCountdown(5);
    });

    r.onMessage('gameError', (data) => {
      console.error('Game Error:', data);
      setError(`Game Error: ${data.message || data.error || 'Unknown error occurred'}`);
      setGameActive(false);
      setCurrentQuestion(null);
    });
    
    r.onMessage('roomClosed', (data) => {
      console.log('Room closed:', data);
      // Room is being closed, no need to do anything as we're already redirecting
    });
    r.onLeave(() => {
      setIsConnected(false);
      setRoom(null);
      setGameActive(false);
      setCurrentQuestion(null);
    });
    r.onError((code, message) => setError(`Room error: ${message}`));
  };


  const primeFromState = (r) => {
    try {
      const st = r.state;
      setGameActive(!!st.gameActive);
      try { console.log('[CLIENT] primeFromState', { gameActive: st.gameActive }); } catch {}
      // Read players immediately
      if (st && st.players && typeof st.players.forEach === 'function') {
        const live = [];
        try {
          st.players.forEach((p, id) => {
            live.push({
              id, // Colyseus session ID
              userId: p.userId, // Django user ID
              name: p.name,
              score: p.score,
              correctAnswers: p.correctAnswers,
              totalAnswers: p.totalAnswers,
              accuracy: p.totalAnswers > 0 ? (p.correctAnswers / p.totalAnswers * 100).toFixed(1) : '0',
            });
          });
        } catch {}
        setPlayers(live);
        try {
          const anyAnswered = live.some(p => (p.totalAnswers || 0) > 0);
          if (anyAnswered) setHasCompletedFirstQuestion(true);
        } catch {}
      }
      // If a question is already active in state, populate it
      if (st && st.currentQuestion && st.currentQuestion.question) {
        setQuestionActive(true);
        setCurrentQuestion(st.currentQuestion.question);
        setQuestionType(st.currentQuestion.questionType || '');
        setQuestionCategoryType(st.currentQuestion.category_type || ''); // Read category_type from state
        setOptions({
          A: st.currentQuestion.optionA || '',
          B: st.currentQuestion.optionB || '',
          C: st.currentQuestion.optionC || '',
          D: st.currentQuestion.optionD || '',
        });
        // Set question number from state if available
        if (typeof st.questionNumber === 'number' && st.questionNumber > 0) {
          setQuestionNumber(st.questionNumber);
        }
        const limit = st.currentQuestion.timeLimit || 10;
        const started = st.currentQuestion.startTime || Date.now();
        const elapsed = Math.floor((Date.now() - started) / 1000);
        setTimeRemaining(Math.max(0, limit - elapsed));
        
        // Set difficulty if available in state
        try {
          if (st.currentQuestion && st.currentQuestion.difficulty_level) {
            setCurrentQuestionDifficulty(String(st.currentQuestion.difficulty_level).toLowerCase());
          } else if (difficulty) {
            // Fallback to room difficulty if question difficulty not available
            setCurrentQuestionDifficulty(String(difficulty).toLowerCase());
          }
        } catch {}
      }
    } catch {}
  };

  const handleToggleEliminate = (optionValue, e) => {
    e.stopPropagation();
    if (result) return; // Don't allow elimination after result is shown
    
    setEliminatedOptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(optionValue)) {
        newSet.delete(optionValue);
        // If this option was selected, clear selection
        if (answer === optionValue) {
          setAnswer('');
        }
      } else {
        newSet.add(optionValue);
        // If this option was selected, clear selection
        if (answer === optionValue) {
          setAnswer('');
        }
      }
      return newSet;
    });
  };

  const submitAnswer = () => {
    if (room && isConnected && answer.trim() !== '') {
      console.log('submitAnswer', answer);
      setLeaveLocked(true);
      room.send('submitAnswer', { answer: answer});
    }
  };


  // Changed for submitting with the freeze timer
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && answer.trim() !== '' && !submitDisabled) 
    {
      submitAnswer();
    } 
    else if (e.key === 'Enter' && submitDisabled) 
    {
      e.preventDefault(); // stop form from submitting early
    }
  };

  const handleLeave = async () => {
    try {
      // Find the current player using userId (Django user ID)
      const currentPlayer = players.find(p => p.userId === user?.user_id);
      
      if (currentPlayer) {
        updateRoomMember(user.user_id, roomId, null, currentPlayer.score || 0, currentPlayer.accuracy || 0);
      } else {
        console.warn('Could not find current player in players array, skipping room member update');
      }
      
      await room?.leave(); 
    } catch (error) {
      console.log('error', error);
    } finally {
      // Reset countdown if leaving during countdown
      setShowCountdown(false);
      setCountdown(5);
      navigate('/lobby-multiplayer');
    }
  };

  // Memoize gradient for title and headings
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);
  
  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  return (
    <div className="min-h-screen w-full flex flex-col items-center relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      {/* Wrong Answer X Mark Animation */}
      <AnimatePresence>
        {showWrongEffect && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: [0, 1.2, 1] }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ 
                rotate: [0, -10, 10, -10, 10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 0.6,
                times: [0, 0.2, 0.4, 0.6, 0.8, 1]
              }}
              className="bg-red-500/20 dark:bg-red-500/30 rounded-full p-12 sm:p-16 md:p-20 backdrop-blur-sm"
            >
              <X 
                size={360} 
                className="text-red-600 dark:text-red-400 w-[360px] h-[360px] sm:w-[400px] sm:h-[400px] md:w-[480px] md:h-[480px]" 
                strokeWidth={8}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Correct Answer Green Tick Mark Animation */}
      <AnimatePresence>
        {showCorrectEffect && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: [0, 1.2, 1] }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 0.6,
                times: [0, 0.2, 0.4, 0.6, 0.8, 1]
              }}
              className="bg-green-500/20 dark:bg-green-500/30 rounded-full p-12 sm:p-16 md:p-20 backdrop-blur-sm"
            >
              <Check 
                size={360} 
                className="text-green-600 dark:text-green-400 w-[360px] h-[360px] sm:w-[400px] sm:h-[400px] md:w-[480px] md:h-[480px]" 
                strokeWidth={8}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">
        {questionResultSummary && (
          <div ref={roomContainerRef} className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-xl px-4 sm:px-6 md:px-8 py-6 sm:py-8 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto border border-white/20 dark:border-gray-700/50">
              <div className="text-center mb-6">
                <h3
                  key={`round-results-${isDarkMode}`}
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-zonaProUltra relative inline-block mb-4 sm:mb-6"
                  style={{
                    color: isDarkMode ? '#ff7a00' : '#212D82',
                    background: sectionHeadingGradient,
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'shine 10s linear infinite',
                    lineHeight: '1.2',
                    paddingBottom: '0.25rem'
                  }}
                >
                Round Results
              </h3>
              </div>
              <div className="space-y-3 sm:space-y-4">
                {questionResultSummary.winners.length > 0 && (
                  <div className="text-base sm:text-lg text-green-600 dark:text-green-400 font-bold font-zonaProUltra">
                    {questionResultSummary.winners.map(w => w.name).join(', ')} answered first in {Math.floor(questionResultSummary.winnerTime / 1000)} seconds!
                  </div>
                )}
                <h4 className="text-lg sm:text-xl font-bold text-gray-700 dark:text-gray-300 font-zonaProUltra">All Answers:</h4>
                <ul className="list-none p-0 space-y-2">
                  {Object.entries(questionResultSummary.playerTimes)
                    .sort(([idA, dataA], [idB, dataB]) => dataA.time - dataB.time)
                    .map(([id, data]) => {
                      const player = players.find(p => p.id === id);
                      const delta = (questionResultSummary.deltas && questionResultSummary.deltas[id]) ?? 0;
                      const deltaText = delta > 0 ? `+${delta}` : `${delta}`;
                      const deltaColor = delta > 0 ? 'text-green-600 dark:text-green-400' : delta < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400';

                      return (
                        <li key={id} className="grid grid-cols-2 gap-2 sm:gap-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm sm:text-base md:text-lg text-gray-800 dark:text-gray-200">
                          <span className="font-zonaProRegular">{player?.name || 'Unknown'}</span>
                          <span className="font-zonaProRegular">{Math.floor(data.time / 1000)}s - {data.isCorrect ? '✅ Correct' : '❌ Incorrect'}</span>
                        </li>
                      );
                    })}
                </ul>
              </div>
              {gameActive && (
              <p className="mt-6 sm:mt-8 text-lg sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-200 text-center font-zonaProUltra">
                Next Round Starts In: <span className="text-cyan-600 dark:text-orange-500 animate-bounce">{summaryCountdown}s</span>
              </p>
              )}
            </div>
              </div>
            )}

        {/* Component 1: Title & Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`game-room-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Game Room
          </h1>
          <motion.div
            className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div>Room Code: <span className="font-semibold">{roomId}</span></div>
            {subject && (
              <div className="sm:inline sm:before:content-['•'] sm:before:mx-2">
                Subject: <span className="font-semibold">{subject}</span>
              </div>
            )}
            {roomPassword && (
              <div className="sm:inline sm:before:content-['•'] sm:before:mx-2">
                Password: <span className="font-semibold">●●●●●●</span>
          </div>
            )}
          </motion.div>
        </motion.div>

        {/* Component 2: Header Cards - Room Name, Timer, Score, Total Score, Status */}
        <div className="w-full max-w-7xl mx-auto mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
            <Card delay={0.1} className="text-center p-3 sm:p-4 md:p-6">
              <div className="flex flex-col items-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cyan-500 dark:text-orange-500 mb-1 sm:mb-2" />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-1">Room</span>
                <p className="text-sm sm:text-base md:text-lg font-bold text-cyan-500 dark:text-orange-500 font-zonaProUltra truncate w-full">
                  {roomName || 'Multiplayer Game'}
                </p>
            </div>
            </Card>
            <Card delay={0.15} className="text-center p-3 sm:p-4 md:p-6">
              <div className="flex flex-col items-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cyan-500 dark:text-orange-500 mb-1 sm:mb-2" />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-1">Timer</span>
                <p className={`text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra ${(timeRemaining <= 5 && timeRemaining > 0) ? 'text-red-500 dark:text-red-400 animate-pulse' : 'text-gray-700 dark:text-white'}`}>
                  {timeRemaining}s
                </p>
              </div>
            </Card>
            <Card delay={0.2} className="text-center p-3 sm:p-4 md:p-6">
              <div className="flex flex-col items-center">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-orange-500 dark:text-orange-400 mb-1 sm:mb-2" />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-1">Room Score</span>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-orange-500 dark:text-orange-400 font-zonaProUltra">{myRoomScore}</p>
            </div>
            </Card>
            <Card delay={0.25} className="text-center p-3 sm:p-4 md:p-6">
              <div className="flex flex-col items-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2" style={{ color: '#ff7a00' }} />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-1">Total Score</span>
                <p className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra" style={{ color: '#ff7a00' }}>{totalScore.toLocaleString()}</p>
          </div>
            </Card>
            <Card delay={0.3} className="text-center p-3 sm:p-4 md:p-6">
              <div className="flex flex-col items-center">
                <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2 flex items-center justify-center">
                  <div className={`w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 rounded-full ${isConnected ? 'bg-green-500 dark:bg-green-400' : 'bg-red-500 dark:bg-red-400'} shadow-lg relative group`}>
                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      {isConnected ? 'Connected' : 'Disconnected'}
                    </span>
              </div>
                </div>
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-1">Status</span>
                <p className={`text-[10px] sm:text-xs font-semibold font-zonaProRegular ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {isConnected ? 'Online' : 'Offline'}
                </p>
                      </div>
            </Card>
            </div>
          </div>

        {/* Component 3: Question Container from PlayGamePage.js */}
        {!questionResultSummary && !showRoundWinnerTransition && gameActive && currentQuestion && (
          <Card delay={0.4} className="mb-6 w-full p-4 sm:p-5 md:p-6" style={{ borderRadius: '8px', boxShadow: getDifficultyGlow() }}>
          <div className="md:flex md:gap-8">
            {/* Left: Question and Feedback */}
            <div className="md:w-3/5 lg:w-1/2 space-y-4 sm:space-y-5">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-white font-zonaProUltra">Question {questionNumber}:</h1>
                      {(() => {
                        const qDiff = currentQuestionDifficulty?.toLowerCase() || difficulty?.toLowerCase() || 'easy';
                        const difficultyColors = {
                          easy: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-400 dark:border-green-600',
                          medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-400 dark:border-yellow-600',
                          hard: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-400 dark:border-red-600'
                        };
                        const colors = difficultyColors[qDiff] || difficultyColors.easy;
                        return (
                          <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border-2 ${colors}`}>
                            {qDiff.charAt(0).toUpperCase() + qDiff.slice(1)}
                          </div>
                        );
                      })()}
                      {(() => {
                        // Use category_type from the question to determine if it's math
                        const categoryTypeLower = questionCategoryType?.toLowerCase() || '';
                        const isMathCategory = categoryTypeLower === 'math' || 
                                              categoryTypeLower.includes('math') ||
                                              categoryTypeLower === 'algebra' ||
                                              categoryTypeLower === 'advanced math' ||
                                              categoryTypeLower === 'geometry and trigonometry' ||
                                              categoryTypeLower === 'problem solving and data analysis';
                        return isMathCategory ? (
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsCalculatorOpen(!isCalculatorOpen);
                            }}
                            className={`p-2 rounded-lg transition-all duration-200 ${
                              isCalculatorOpen
                                ? 'bg-[#ff9e42] text-white'
                                : isDarkMode
                                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                            }`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Open Calculator"
                          >
                            <CalculatorIcon className="w-5 h-5" />
                          </motion.button>
                        ) : null;
                      })()}
                    </div>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-200 mb-3">Read the question carefully and select the best answer.</p>
                <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-100 text-left leading-relaxed whitespace-pre-line">
              {currentQuestion}
              </p>

              {result && (
                  <div className={`mt-4 sm:mt-6 p-4 sm:p-5 rounded-lg border-l-4 ${result.correct ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
                    <h3 className={`text-lg sm:text-xl font-semibold mb-2 font-zonaProUltra ${result.correct ? 'text-green-700 dark:text-green-200' : 'text-red-700 dark:text-red-200'}`}>
                    {result.correct ? 'CORRECT!' : 'INCORRECT'}
                  </h3>
                    <p className={`text-sm sm:text-base mb-2 ${result.correct ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {result.correct
                      ? result.feedback
                      : (questionType === 'Multiple Choice'
                          ? `The correct answer was "${result.correctAnswer}", ${result.feedback}`
                          : `The correct answer was ${result.correctAnswer}, ${result.feedback}`)}
                  </p>
                  </div>
              )}
            </div>

            {/* Right: Options / Input + Submit */}
            <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6 mt-4 md:mt-0">
                <h2 className="text-base sm:text-lg md:text-xl font-zonaProUltra text-gray-700 dark:text-gray-100 mb-3 sm:mb-4">
                {result ? 'Results:' : 'Choose an Answer:'}
              </h2>
              {result ? (
                questionType === 'Multiple Choice' ? (
                  <div className="space-y-3">
                    {Object.entries(options).map(([key, value]) => {
                      if (!value) return null;
                      const isSelected = answer === value;
                      const isCorrectAnswer = value === result.correctAnswer;
                      let feedbackClasses = 'w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base md:text-lg cursor-not-allowed font-medium';
                      if (isSelected) {
                              feedbackClasses += isCorrectAnswer ? ' bg-green-50 dark:bg-green-900/30 border-green-400 dark:border-green-600 text-green-700 dark:text-green-300' : ' bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300';
                      } else if (isCorrectAnswer) {
                              feedbackClasses += ' bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300';
                      } else {
                              feedbackClasses += ' bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 opacity-75';
                      }
                      return (
                        <button key={key} type="button" className={feedbackClasses} disabled>
                          <span className="font-bold mr-2">{key})</span>{value}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                          <div className={`text-lg font-semibold p-3 rounded-xl mt-4 ${result.correct ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                      Your Answer: {answer}
                    </div>
                    {!result.correct && (
                            <div className="text-lg font-semibold p-3 rounded-xl mt-3 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                        Correct Answer: {result.correctAnswer}
                      </div>
                    )}
                  </>
                )
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); submitAnswer(); }} className="space-y-3">
                  {questionType === 'Multiple Choice' ? (
                    <div className="grid grid-cols-1 gap-3">
                      {Object.entries(options).map(([key, value]) => {
                        if (!value) return null;
                        const isEliminated = eliminatedOptions.has(value);
                        const base = 'flex-1 text-left p-3 sm:p-4 rounded-lg border-2 focus:outline-none text-sm sm:text-base md:text-lg transition-colors duration-150 ease-in-out';
                        const selected = '!bg-[#ff9e42] !border-[#ff9e42] text-white font-bold shadow-md ring-2 ring-[#ff9e42]/30';
                        const unselected = 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-[#ff9e42] focus:border-[#ff9e42] focus:ring-1 focus:ring-[#ff9e42]/20 active:bg-[#ff9e42] active:border-[#ff9e42] active:text-white';
                        const eliminatedClasses = isEliminated ? "opacity-50 cursor-not-allowed line-through" : "";
                        const final = `${base} ${answer === value ? selected : unselected} ${eliminatedClasses}`;
                        return (
                          <div key={key} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                if (!isEliminated) {
                                  setAnswer(value);
                                }
                              }}
                              className={final}
                              disabled={isEliminated}
                              style={answer === value ? { backgroundColor: '#ff9e42', borderColor: '#ff9e42', color: 'white' } : {}}
                            >
                              <span className={`${isEliminated ? 'line-through' : ''}`}>
                                <span className="font-bold mr-2">{key})</span>{value}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleToggleEliminate(value, e)}
                              className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 flex-shrink-0 ${
                                isEliminated
                                  ? 'bg-red-500 hover:bg-red-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
                              }`}
                              title={isEliminated ? "Restore option" : "Eliminate option"}
                              disabled={!!result}
                            >
                              <X className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="Enter your answer..."
                      value={answer}
                        onChange={(e) => {
                          setAnswer(e.target.value);
                        }}
                      onKeyPress={handleKeyPress}
                        className="w-full p-2 sm:p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base md:text-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                    />
                  )}
                    <button
                    type="submit"
                          disabled={answer.trim() === '' || submitDisabled}
                          className={`mt-4 sm:mt-6 w-full py-2 sm:py-3 px-4 sm:px-6 text-sm sm:text-base md:text-lg lg:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:ring-opacity-50 transition-all
                            ${submitDisabled
                                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed text-white'
                                : 'bg-gradient-to-r from-gray-900 to-black dark:from-gray-800 dark:to-gray-900 text-white hover:shadow-lg hover:shadow-gray-900/50'}`}
                        >
                          {submitDisabled ? `Wait ${submitCountdown}s...` : 'SUBMIT'}
                    </button>
                </form>
              )}
            </div>
          </div>
              </Card>
        )}

        {/* Components 4 & 5: Points at Stake (Left) and Scoreboard (Right) - 2 Columns */}
        {!questionResultSummary && !showRoundWinnerTransition && gameActive && currentQuestion && players.length > 0 && (
          <div className="w-full max-w-7xl mx-auto mt-4 sm:mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Component 4: Left Column - Points at Stake Display */}
              {(() => {
                  const qDiff = currentQuestionDifficulty?.toLowerCase() || difficulty?.toLowerCase() || 'easy';
                  const colors = getDifficultyColors();
                  
                  return (
                  <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.5,
                      type: "spring",
                      stiffness: 100,
                    }}
                    className={`relative backdrop-blur-xl rounded-3xl shadow-2xl border transition-all duration-700 ease-in-out ${colors.bg} ${colors.border} border-2 sm:border-4 p-4 sm:p-5 md:p-6`}
                    style={{
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                      <div className="flex flex-col items-center justify-center gap-3 sm:gap-4">
                        <div className="text-center">
                        <p className="text-xs sm:text-sm md:text-base uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2 font-zonaProRegular">Points at Stake</p>
                          <div className="flex items-center justify-center gap-2 sm:gap-3">
                            <div className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ${colors.text} font-zonaProUltra`}
                                 style={{ animation: 'numberPop 0.8s ease-out' }}>
                              {animatedPoints}
                            </div>
                            <div className={`text-lg sm:text-xl md:text-2xl ${colors.text} font-zonaProUltra transition-opacity duration-300`}>
                              points
                            </div>
                          </div>
                        </div>
                        
                        <div className="w-full h-px bg-gray-300 dark:bg-gray-600 opacity-50"></div>
                        
                        <div className="flex flex-col items-center gap-2">
                          <p className="text-xs sm:text-sm uppercase tracking-wider text-gray-600 dark:text-gray-400 font-zonaProRegular">Difficulty</p>
                          <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full ${colors.bg} ${colors.border} border-2 transform transition-all duration-300 hover:scale-110`}>
                            <span className={`text-sm sm:text-base md:text-lg font-bold ${colors.text} font-zonaProUltra capitalize`}>
                              {qDiff}
                            </span>
                          </div>
                        </div>
                        
                        <div className="w-full h-px bg-gray-300 dark:bg-gray-600 opacity-50"></div>
                        
                        <div className="flex flex-col items-center gap-2">
                          <p className="text-xs sm:text-sm uppercase tracking-wider text-gray-600 dark:text-gray-400 font-zonaProRegular">Reward System</p>
                          <div className="flex gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div className="flex items-center gap-1 transform transition-all duration-300 hover:scale-110">
                              <span className="text-green-600 dark:text-green-400 font-bold text-base sm:text-lg animate-pulse">✓</span>
                              <span className="text-gray-700 dark:text-gray-300 font-zonaProRegular font-semibold">+{getPointsAtStake()}</span>
                            </div>
                            <span className="text-gray-400 dark:text-gray-500">|</span>
                            <div className="flex items-center gap-1 transform transition-all duration-300 hover:scale-110">
                              <span className="text-red-600 dark:text-red-400 font-bold text-base sm:text-lg">✗</span>
                              <span className="text-gray-700 dark:text-gray-300 font-zonaProRegular font-semibold">-{getPointsAtStake()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })()}

              {/* Component 5: Right Column - Scoreboard with old styling but animated */}
              <Card delay={0.6} className="mb-6 p-4 sm:p-5 md:p-6">
                <div className="mb-3 sm:mb-4">
                  <h2 
                    key={`leaderboard-${isDarkMode}`}
                    className="text-xl sm:text-2xl font-bold font-zonaProUltra text-gray-800 dark:text-white" 
                    style={{
                      color: isDarkMode ? '#ff7a00' : '#212D82',
                      background: sectionHeadingGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite'
                    }}
                  >
                    Leaderboard
                  </h2>
                </div>
              {(finalPlayers.length === 0 && players.length === 0) ? (
                  <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 text-center py-6 sm:py-8">No players connected</p>
              ) : finalPlayers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Rank</th>
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Name</th>
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortPlayersByScoreAndAccuracy([...finalPlayers])
                          .map((p, idx) => {
                            const rank = idx + 1;
                            const isTopThree = rank <= 3;
                            const textClass = isTopThree ? 'font-zonaProUltra font-bold text-base sm:text-lg md:text-xl' : 'font-zonaProRegular text-sm sm:text-base';
                            let rowClass = 'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors';
                            if (rank === 1) rowClass += ' bg-yellow-50 dark:bg-yellow-900/20';
                            else if (rank === 2) rowClass += ' bg-gray-50 dark:bg-gray-800/50';
                            else if (rank === 3) rowClass += ' bg-orange-50 dark:bg-orange-900/20';
                            if (p.id === myId) rowClass += ' bg-cyan-100 dark:bg-cyan-900/30';

                            const rankIcon = rank === 1
                              ? <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" aria-label="1st place" />
                              : rank === 2
                                ? <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" aria-label="2nd place" />
                                : rank === 3
                                  ? <Crown className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#cd7f32' }} aria-label="3rd place" />
                                  : <span className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{rank}</span>;

                            return (
                              <tr key={p.id} className={rowClass}>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass}`}>{rankIcon}</td>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass} text-gray-800 dark:text-gray-200 text-left`}>
                                   <span className="inline-flex items-center gap-1 sm:gap-2">
                                     {p.name}
                                     {!!recentCorrectIds[p.id] && (
                                       <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 animate-bounce" />
                                     )}
                                    {p.waiting && <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">(waiting)</span>}
                                   </span>
                                 </td>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass} text-gray-800 dark:text-gray-200`}>
                                  <AnimatedNumber
                                    value={p.score}
                                    fromValue={(typeof roundDeltasRef.current[p.id] === 'number') ? (p.score - (roundDeltasRef.current[p.id] || 0)) : (lastScoresRef.current[p.id] ?? p.score)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                </div>
              ) : gameActive ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Rank</th>
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Name</th>
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players
                          .slice()
                          .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      const accuracyA = parseFloat(a.accuracy) || 0;
      const accuracyB = parseFloat(b.accuracy) || 0;
      return accuracyB - accuracyA;
    })
                          .map((p, idx) => {
                            const rank = idx + 1;
                            const isTopThree = rank <= 3;
                            const textClass = isTopThree ? 'font-zonaProUltra font-bold text-base sm:text-lg md:text-xl' : 'font-zonaProRegular text-sm sm:text-base';
                            let rowClass = 'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors';
                            if (rank === 1) rowClass += ' bg-yellow-50 dark:bg-yellow-900/20';
                            else if (rank === 2) rowClass += ' bg-gray-50 dark:bg-gray-800/50';
                            else if (rank === 3) rowClass += ' bg-orange-50 dark:bg-orange-900/20';
                            if (p.id === myId) rowClass += ' bg-cyan-100 dark:bg-cyan-900/30';

                            const rankIcon = rank === 1
                              ? <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" aria-label="1st place" />
                              : rank === 2
                                ? <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" aria-label="2nd place" />
                                : rank === 3
                                  ? <Crown className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#cd7f32' }} aria-label="3rd place" />
                                  : <span className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{rank}</span>;

                            return (
                              <tr key={p.id} className={rowClass}>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass}`}>{rankIcon}</td>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass} text-gray-800 dark:text-gray-200 text-left`}>
                                   <span className="inline-flex items-center gap-1 sm:gap-2">
                                     {p.name}
                                     {!!recentCorrectIds[p.id] && (
                                       <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 animate-bounce" />
                                     )}
                                    {p.waiting && <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">(waiting)</span>}
                                   </span>
                                 </td>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass} text-gray-800 dark:text-gray-200`}>
                                  <AnimatedNumber
                                    value={p.score}
                                    fromValue={(typeof roundDeltasRef.current[p.id] === 'number') ? (p.score - (roundDeltasRef.current[p.id] || 0)) : (lastScoresRef.current[p.id] ?? p.score)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                </div>
              ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Rank</th>
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Name</th>
                          <th className="font-zonaProBlack text-left py-2 sm:py-3 px-2 sm:px-4 text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players
                          .slice()
                          .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      const accuracyA = parseFloat(a.accuracy) || 0;
      const accuracyB = parseFloat(b.accuracy) || 0;
      return accuracyB - accuracyA;
    })
                          .map((p, idx) => {
                            const rank = idx + 1;
                            const isTopThree = rank <= 3;
                            const textClass = isTopThree ? 'font-zonaProUltra font-bold text-base sm:text-lg md:text-xl' : 'font-zonaProRegular text-sm sm:text-base';
                            let rowClass = 'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors';
                            if (rank === 1) rowClass += ' bg-yellow-50 dark:bg-yellow-900/20';
                            else if (rank === 2) rowClass += ' bg-gray-50 dark:bg-gray-800/50';
                            else if (rank === 3) rowClass += ' bg-orange-50 dark:bg-orange-900/20';
                            if (p.id === myId) rowClass += ' bg-cyan-100 dark:bg-cyan-900/30';

                            const rankIcon = rank === 1
                              ? <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" aria-label="1st place" />
                              : rank === 2
                                ? <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" aria-label="2nd place" />
                                : rank === 3
                                  ? <Crown className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#cd7f32' }} aria-label="3rd place" />
                                  : <span className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{rank}</span>;

                            return (
                              <tr key={p.id} className={rowClass}>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass}`}>{rankIcon}</td>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass} text-gray-800 dark:text-gray-200 text-left`}>
                                   <span className="inline-flex items-center gap-1 sm:gap-2">
                                     {p.name}
                                     {!!recentCorrectIds[p.id] && (
                                       <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 animate-bounce" />
                                     )}
                                    {p.waiting && <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">(waiting)</span>}
                                   </span>
                                 </td>
                                <td className={`py-2 sm:py-3 px-2 sm:px-4 ${textClass} text-gray-800 dark:text-gray-200`}>
                                  <AnimatedNumber
                                    value={p.score}
                                    fromValue={(typeof roundDeltasRef.current[p.id] === 'number') ? (p.score - (roundDeltasRef.current[p.id] || 0)) : (lastScoresRef.current[p.id] ?? p.score)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                </div>
            )}
                </Card>
              </div>
          </div>
            )}
        {/* Round Winner Display */}
        {roundWinner && (
          <div className="mt-6 mb-4">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-2xl p-6 text-center shadow-lg animate-pulse">
              <div className="flex items-center justify-center space-x-3">
                <Trophy className="w-8 h-8 text-yellow-200" />
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold font-zonaProUltra">
                    🎉 {roundWinner} Won the Round! 🎉
                  </h3>
                  <p className="text-lg text-yellow-100 mt-1">
                    First to answer correctly!
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-yellow-200" />
              </div>
            </div>
          </div>
        )}
        {/* Result feedback
        {result && (
          <div className={`mt-6 p-5 rounded-lg border-l-4 ${result.correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
            <h3 className={`text-xl font-semibold mb-2 font-zonaProUltra ${result.correct ? 'text-green-700' : 'text-red-700'}`}>
              {result.correct ? 'CORRECT!' : 'INCORRECT'}
            </h3>
            <p className={`${result.correct ? 'text-green-600' : 'text-red-600'}`}>
              {result.correct
                ? result.feedback
                : questionType === 'Multiple Choice'
                  ? `The correct answer was "${result.correctAnswer}", ${result.feedback}`
                  : `The correct answer was ${result.correctAnswer}, ${result.feedback}`}
            </p>
          </div>
        )} */}
        {/* Countdown for report generation */}
        {showCountdown && (
          <div className="mt-6">
            <div className="relative mx-auto h-[360px] md:h-[420px] rounded-full shadow-inner bg-gradient-to-br from-cyan-50 to-green-50 border-4 border-cyan-200 flex items-center justify-center"
                 style={{background: 'radial-gradient(circle at center, rgba(0,209,255,0.15), rgba(50,232,117,0.12) 40%, rgba(11,16,32,0.08) 70%)'}}>
              <div className="text-center">
                <div className="text-6xl md:text-8xl font-bold text-cyan-600 mb-4 animate-pulse">
                  {countdown}
                </div>
                <div className="text-2xl md:text-3xl font-zonaProUltra text-gray-700 mb-2">
                  Your Game Report is Being Generated
                </div>
                <div className="text-lg text-gray-600 mb-2">
                  Redirecting to report page in {countdown} second{countdown !== 1 ? 's' : ''}...
                </div>
                <div className="text-sm text-gray-500">
                  Room will be closed after redirect
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Round Winner Display */}
        {roundWinner && (
          <div className="mt-6 mb-4">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-2xl p-6 text-center shadow-lg animate-pulse">
              <div className="flex items-center justify-center space-x-3">
                <Trophy className="w-8 h-8 text-yellow-200" />
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold font-zonaProUltra">
                    🎉 {roundWinner} Won the Round! 🎉
                  </h3>
                  <p className="text-lg text-yellow-100 mt-1">
                    First to answer correctly!
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-yellow-200" />
              </div>
            </div>
          </div>
        )}

        {/* Loading Screen - Show when game is active but no question yet */}
        {gameActive && currentQuestion === null && !showCountdown && (
          <div className="mt-6">
            <div className="relative mx-auto h-[360px] md:h-[420px] rounded-full shadow-inner bg-black/50 backdrop-blur-sm flex items-center justify-center"
                 style={{background: 'radial-gradient(circle at center, rgba(0,209,255,0.12), rgba(50,232,117,0.08) 40%, rgba(11,16,32,0.06) 70%)'}}>
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                <div className="text-gray-500 text-2xl md:text-3xl font-zonaProUltra">Loading Question...</div>
              </div>
            </div>
          </div>
        )}


        <div className="mt-4 sm:mt-6 flex justify-end">
          <button 
            onClick={() => setShowEndSessionModal(true)} 
              disabled={showCountdown || leaveLocked}
            className={`py-2 sm:py-3 px-4 sm:px-6 text-white text-sm sm:text-base md:text-lg font-zonaProUltra rounded-full transition-colors duration-150 ${
                showCountdown || leaveLocked
                ? 'bg-gray-400 cursor-not-allowed' 
                  : (players.length === 1 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-red-600 hover:bg-red-700')
              }`}
              title={leaveLocked ? 'Leaving is disabled until the next question appears' : undefined}
            >
              {showCountdown
                ? 'Generating Report...'
                : leaveLocked
                  ? 'Next Question starting...'
                  : (players.length === 1 ? 'End Game' : 'Leave Room')}
          </button>
        </div>
      </div>
      {/*End session confirmation modal */}
       {showEndSessionModal && !showCountdown && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-xl px-4 sm:px-6 md:px-8 py-6 sm:py-8 w-full max-w-md mx-4 border border-white/20 dark:border-gray-700/50">
            <div className="text-center mb-4 sm:mb-6">
              <h2
                key={`end-session-modal-${isDarkMode}`}
                className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra relative inline-block mb-3 sm:mb-4"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite',
                  lineHeight: '1.2',
                  paddingBottom: '0.25rem'
                }}
              >
              {players.length === 1 ? 'End Game' : 'Leave Room'}
            </h2>
            </div>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 text-center font-zonaProRegular">
              {players.length === 1 
                ? 'Are you sure you want to end the game? You will be redirected to the report page.'
                : 'Are you sure you want to leave the room? You will be redirected to the lobby.'
              }
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <button
                onClick={() => {
                  setShowEndSessionModal(false);
                }}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-full font-zonaProUltra text-sm sm:text-base text-gray-700 dark:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (players.length === 1) {
                    // End game - send message to server
                    console.log('Ending game');
                    if (room && isConnected) {
                      room.send('endGame', {});
                    }
                    setShowEndSessionModal(false);
                  } else {
                    // Leave room
                    console.log('Leaving room');
                    handleLeave();
                    setShowEndSessionModal(false);
                  }
                }}
                className={`px-4 sm:px-6 py-2 sm:py-3 text-white rounded-full font-zonaProUltra text-sm sm:text-base transition-colors ${
                  players.length === 1 
                    ? 'bg-orange-600 hover:bg-orange-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {players.length === 1 ? 'End Game' : 'Leave Room'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculator */}
      <Calculator
        isOpen={isCalculatorOpen}
        onClose={() => setIsCalculatorOpen(false)}
        categoryType={questionCategoryType || subject}
      />
    </div>
  );
};

export default MultiplayerGameRoom;
